import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { testConnection } from './Connections';
import { toast } from 'react-toastify';
import Cookies from 'js-cookie';

const OAuthCallback = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const code = params.get('code');
    if (!code) {
      const msg = 'Missing OAuth authorization code.';
      toast.error(msg);
      return navigate('/myconnections', {
        replace: true,
        state: { errorMessage: msg },
      });
    }

    const stored = sessionStorage.getItem('pendingOAuthConnection');
    if (!stored) {
      const msg = 'OAuth state lost. Please retry.';
      toast.error(msg);
      return navigate('/myconnections', {
        replace: true,
        state: { errorMessage: msg },
      });
    }

    const key = `__submitted_oauth_${code}`;
    if (sessionStorage.getItem(key)) return; // prevent duplicate submission
    sessionStorage.setItem(key, 'true');

    const parsed = stored ? JSON.parse(stored) : {};

    const {
      connectionName,
      connector,
      clientID,
      clientSecret,
      instanceUrl,
      redirectUri,
      authType,
      // environment,
    } = parsed;

    console.log('oAuth callback stored:::', parsed);

    let environment = parsed.environment;

    if (!environment) {
      const state = params.get('state');
      try {
        if (state) {
          const decoded = JSON.parse(atob(state));
          console.log('CALLBACK decoded state:', decoded);

          if (decoded.env) environment = decoded.env;
        }
      } catch (e) {
        console.warn('Failed to parse OAuth state', e);
      }
    }

    console.log('oAuth callback resolved env:', environment);

    const payload = {
      user_id: Cookies.get('userId'),
      conn_name: connectionName,
      connector_name: connector,
      custom_params: { instance_url: instanceUrl },
      api_version: null,
      auth: {
        type: authType || 'OAuth2',
        oauth2: {
          grant_type: 'authorization_code',
          client_id: clientID,
          client_secret: clientSecret,
          auth_code: code,
          redirect_uri: redirectUri,
          instance_url: instanceUrl,
        },
      },
      environment,
    };

    testConnection(payload)
      .then((res) => {
        // testConnection already returns response.data, so res is the data
        const data = res;
        console.log('=== OAuth Callback Response Debug ===');
        console.log('Response data:', data);
        console.log('Response type:', typeof data);
        console.log('Response keys:', data && typeof data === 'object' ? Object.keys(data) : 'not an object');
        console.log('test_status:', data?.test_status);
        console.log('status:', data?.status);
        console.log('success:', data?.success);
        console.log('connected:', data?.connected);
        console.log('connection_id:', data?.connection_id || data?.conn_id || data?.id);
        console.log('auth object:', data?.auth);
        console.log('auth.oauth2 object:', data?.auth?.oauth2);
        console.log('access_token:', data?.auth?.oauth2?.access_token || data?.access_token);
        console.log('refresh_token:', data?.auth?.oauth2?.refresh_token || data?.refresh_token);
        console.log('Full response JSON:', JSON.stringify(data, null, 2));
        console.log('=====================================');

        // Check for error messages in various possible fields
        const errMsg =
          data?.detail ||
          data?.error ||
          data?.message ||
          data?.error_message ||
          data?.error_description ||
          (typeof data === 'string' ? data : null);

        if (errMsg) {
          console.error('OAuth callback error from backend:', errMsg);
          toast.error(`OAuth test failed: ${errMsg}`, { autoClose: 5000 });
          sessionStorage.removeItem('pendingOAuthConnection');
          return navigate('/myconnections', {
            replace: true,
            state: { errorMessage: errMsg },
          });
        }

        // Check for access_token and refresh_token presence
        const accessToken = data?.auth?.oauth2?.access_token || data?.access_token;
        const refreshToken = data?.auth?.oauth2?.refresh_token || data?.refresh_token;
        
        console.log('Token check - access_token present:', !!accessToken);
        console.log('Token check - refresh_token present:', !!refreshToken);
        
        // Check for success indicators - multiple possible formats
        const isSuccess =
          data?.test_status === 'success' ||
          data?.test_status === true ||
          data?.status === 'success' ||
          data?.success === true ||
          data?.connected === true ||
          data?.connection_id ||
          data?.conn_id ||
          data?.id || // Connection ID might be in 'id' field
          accessToken ||
          // If test_status exists and is not explicitly failed/false, consider it success
          (data?.test_status !== undefined && 
           data?.test_status !== false && 
           data?.test_status !== 'failed' &&
           data?.test_status !== 'error');

        if (isSuccess) {
          // Warn if tokens are missing (but still consider it success if test_status is good)
          if (!accessToken) {
            console.warn('⚠️ OAuth success but access_token is missing from response');
          }
          if (!refreshToken) {
            console.warn('⚠️ OAuth success but refresh_token is missing from response');
          }
          
          if (accessToken && refreshToken) {
            toast.success('OAuth connection successful! Tokens received.', { autoClose: 2000 });
          } else if (accessToken) {
            toast.success('OAuth connection successful! (refresh_token missing)', { autoClose: 3000 });
          } else {
            toast.warning('OAuth connection successful but tokens not found in response.', { autoClose: 4000 });
          }
          
          sessionStorage.removeItem('pendingOAuthConnection');
          navigate('/myconnections', { replace: true });
        } else if (data?.test_status === 'failed' || data?.test_status === false) {
          const failureMsg = data?.message || data?.detail || 'OAuth connection test failed';
          console.error('OAuth test failed:', failureMsg);
          toast.error(`OAuth test failed: ${failureMsg}`, { autoClose: 5000 });
          sessionStorage.removeItem('pendingOAuthConnection');
          navigate('/myconnections', {
            replace: true,
            state: { errorMessage: failureMsg },
          });
        } else {
          // No clear status - log full response for debugging
          console.warn('OAuth callback: Ambiguous response, no clear status:', data);
          const warningMsg = data?.message || 
            (data?.test_status !== undefined 
              ? `OAuth returned status: ${data.test_status}` 
              : 'OAuth test returned no status. Please check the connection.');
          toast.warning(warningMsg, { autoClose: 5000 });
          sessionStorage.removeItem('pendingOAuthConnection');
          navigate('/myconnections', {
            replace: true,
            state: { 
              errorMessage: warningMsg,
              responseData: data // Include response for debugging
            },
          });
        }
      })
      .catch((err) => {
        console.error('OAuth callback catch error:', err);
        console.error('Error response:', err?.response);
        console.error('Error data:', err?.response?.data);
        
        // Extract error message from various possible locations
        const msg =
          err?.response?.data?.detail ||
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.response?.data?.error_message ||
          err?.response?.data?.error_description ||
          err?.message ||
          (err?.response?.status 
            ? `Server error (${err.response.status}): ${err.response.statusText || 'Unknown error'}`
            : 'OAuth connection failed. Please try again.');
        
        toast.error(`OAuth test failed: ${msg}`, { autoClose: 5000 });
        sessionStorage.removeItem('pendingOAuthConnection');
        navigate('/myconnections', {
          replace: true,
          state: { errorMessage: msg },
        });
      });
  }, []);

  return <p style={{ padding: '2rem' }}>Completing OAuth flow...</p>;
};

export default OAuthCallback;
